import type { ListTeamMembersForWorkspaceMemberFn } from '@/features/iam'
import { type Job } from '@/features/jobs/Job'
import { action, observable } from 'mobx'
import { required, validationContext } from 'validx'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { type TeamMember } from '@/features/iam/teams/TeamMember'

/**
 * Picks the client team a job is transferred to.
 */
export class ClientTeamInputEditorViewModel {
  /**
   * The client team member the jobs are transferred to; their membership carries the team.
   */
  @observable
  accessor clientTeamMember: TeamMember | undefined = undefined

  /**
   * The jobs being transferred.
   */
  @observable
  accessor jobs: Job[] = []

  /**
   * Validation context for client team.
   */
  readonly validationClientTeamMember = validationContext(this, {
    clientTeamMember: [required('Client team is required')],
  })

  constructor(
    private readonly _findTeamMembersByUserPublicId: ListTeamMembersForWorkspaceMemberFn,
  ) {}

  /**
   * Sets client TeamMember to transfer.
   */
  @action.bound
  setClientTeamMember(clientTeamMember?: TeamMember) {
    this.clientTeamMember = clientTeamMember
  }

  /**
   * Indicates if client TeamMember is valid.
   */
  @action.bound
  validateClientTeamMember() {
    return this.validationClientTeamMember.reset().validate().isValid
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.validationClientTeamMember.reset()
    this.clientTeamMember = undefined
  }

  /**
   * Sets the jobs being transferred.
   */
  @action.bound
  setJobs(jobs: Job[]) {
    this.jobs = jobs
  }

  /**
   * Fetches the client teams every job owner belongs to.
   */
  fetchClientTeamMembers = async (
    searchText: string,
    params: Partial<CursorPageQuery>,
    signal?: AbortSignal,
  ): Promise<CursorPaged<TeamMember>> => {
    const collaborators = this.jobs.map((j) => j.collaborators.items).flat()
    const lowerSearchText = searchText.toLowerCase()
    const ownersPublicId = new Set(this.jobs.map((j) => j.owner.user.publicId))

    const result = await Promise.all(
      Array.from(ownersPublicId).map((ownerId) => {
        return this._findTeamMembersByUserPublicId(
          {
            after: params.after,
            limit: params.limit,
            workspaceId: this.jobs[0].workspace.id,
            userPublicId: ownerId,
            includeTeam: true,
          },
          signal,
        )
      }),
    )

    const paged = CursorPaged.of(
      result.reduce((acc, curr) => {
        return acc.filter((item) =>
          curr.some((t) => t.team.id === item.team.id),
        )
      }),
    )

    return CursorPaged.map(paged, (data) =>
      data.filter((t) => {
        const teamInfo = `${t.team.name} ${t.team.id}`
        const invalidTeam = collaborators.some((c) =>
          this.jobs.length === 1
            ? c.team === t.team && c.role === 'Champion' && c.type === 'Client'
            : false,
        )
        return !invalidTeam && teamInfo.toLowerCase().includes(lowerSearchText)
      }),
    )
  }
}
